import Section from 'app/frontend/base/components/Section'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Navigate, useParams } from 'react-router-dom'
import useReferenceStore from 'app/frontend/base/state/useReferenceStore'
import EventHeader from './components/EventHeader'
import {
  useEventActivitiesPlanningInfo,
  usePageInfo,
  useTeamEvent,
} from 'app/frontend/base/config/queries'
import EventActivityBlock from './components/EventActivityBlock'
import { useCallback, useMemo, useState } from 'react'
import AddParticipantModal from './components/AddParticipantModal'
import AddEventActivitiesDialog from './components/AddEventActivitiesDialog'
import useApplyEventUpdate from './hooks/useApplyEventUpdate'
import useOptimisticEventUpdate from './hooks/useOptimisticEventUpdate'
import { AddEventFormValues } from '../TeamEventOverview/components/AddEventDialog'
import { TeamEventUpdateProvider } from './context/TeamEventUpdateContext'
import EventActivityGrid from './components/EventActivityGrid'
import EventSetupModeControl from './components/EventSetupModeControl'
import EventGetStartedBanner from './components/EventGetStartedBanner'
import EventActivitySpecificEncounter from './components/EventActivitySpecificEncounter'
import { resolveEventFocusActivity } from './components/eventFocusActivity'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import {
  TeamEventParticipantsProvider,
  useTeamEventParticipantsContext,
} from './context/TeamEventParticipantsContext'
import {
  TeamEventPlanningInfoProvider,
  useTeamEventPlanningInfoState,
} from './context/TeamEventPlanningInfoContext'
import { Switch } from '@/components/ui/switch'
import Card from 'app/frontend/base/components/Card'
import EventAttendanceSummary from './components/EventAttendanceSummary'
import useTeamEventMutations from './mutations/useTeamEventMutations'
import ViewerSignupCard from './components/ViewerSignupCard'
import {
  CreateEventActivitiesPayload,
  UpdateEventActivityPayload,
} from 'app/frontend/base/api/event'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import ImportFromPreviousEventDialog from './components/ImportFromPreviousEventDialog'
import EventBulkSelectionMenuItem from './components/EventBulkSelectionMenuItem'
import { Settings2Icon } from 'lucide-react'
import useEventActivityLinkedPlanData, {
  EventActivityLinkedPlanData,
} from './hooks/useEventActivityLinkedPlanData'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { buildEventActivityLinkedPlanNote } from './components/eventActivityNoteExport'
import TeamEventSkeleton from './components/TeamEventSkeleton'
import { useEventImportCandidates } from './hooks/useEventImportCandidates'

type TeamEventLinkedPlanDataProviderProps = {
  activities: wowaudit.EventActivity[]
  activityType: wowaudit.Event['activityType']
  children: (data: {
    linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
    isRaidPlansLoading: boolean
  }) => React.ReactNode
}

const TeamEventLinkedPlanDataProvider = ({
  activities,
  activityType,
  children,
}: TeamEventLinkedPlanDataProviderProps) => {
  const { linkedPlanDataByActivityId, isRaidPlansLoading } =
    useEventActivityLinkedPlanData({
      activities,
      enabled: activityType === 'raids',
    })
  return children({ linkedPlanDataByActivityId, isRaidPlansLoading })
}

type CopyAllNotesButtonProps = {
  activities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
}

const CopyAllNotesButton = ({
  activities,
  linkedPlanDataByActivityId,
}: CopyAllNotesButtonProps) => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { allEntries } = useTeamEventParticipantsContext()
  const { copied, copyToClipboard } = useCopyToClipboard()
  const copiedAllNotes = copied !== undefined

  const handleCopyAllNotes = () => {
    const allNotes = activities
      .filter((activity) => activity.active)
      .map((activity) =>
        buildEventActivityLinkedPlanNote({
          activity,
          linkedPlanData: linkedPlanDataByActivityId.get(activity.id),
          allEntries,
        }),
      )
      .filter((note) => note.length > 0)
      .join('\n\n')
    if (!allNotes) return
    copyToClipboard(allNotes)
  }

  const hasLinkedPlans =
    gameVersion === 'live' &&
    activities
      .filter((activity) => activity.active)
      .some(
        (activity) =>
          linkedPlanDataByActivityId.get(activity.id)?.linkedRaidPlan != null,
      )

  if (hasLinkedPlans) {
    return (
      <Button
        size='sm'
        onClick={handleCopyAllNotes}
        className='flex flex-col h-auto py-1 leading-tight'
      >
        <span>{copiedAllNotes ? 'Copied all notes!' : 'Copy all notes'}</span>
        <span className='text-xs opacity-70'>and invites</span>
      </Button>
    )
  }

  return (
    <Button size='sm' onClick={handleCopyAllNotes}>
      {copiedAllNotes ? 'Copied all invites!' : 'Copy all invites'}
    </Button>
  )
}

const TeamEvent = () => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const { team, guild } = useTeam()
  const { eventId } = useParams()
  const { getReference } = useReferenceStore()
  const [addParticipantsOpen, setAddParticipantsOpen] = useState(false)
  const [addActivitiesOpen, setAddActivitiesOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isResetSelectionsConfirmOpen, setIsResetSelectionsConfirmOpen] =
    useState(false)
  const canAdminEdit = accessLevel === 'admin'
  const parsedEventId = Number(eventId)
  const numericEventId = Number.isFinite(parsedEventId)
    ? parsedEventId
    : undefined
  const {
    data: event,
    isLoading: isLoadingEvent,
    isError: hasEventError,
  } = useTeamEvent(team.id, numericEventId)

  const { showPlanningInfo, setShowPlanningInfo } =
    useTeamEventPlanningInfoState(team.id, event)

  const { data: eventActivitiesPlanningInfo } = useEventActivitiesPlanningInfo(
    team.id,
    numericEventId,
    event?.activities || [],
    canAdminEdit || showPlanningInfo,
  )
  const applyEventUpdate = useApplyEventUpdate()
  const optimisticEventUpdate = useOptimisticEventUpdate(numericEventId)
  const {
    updateEventMutation,
    updateStatusMutation,
    updateSelectionMutation,
    removeGuestMutation,
    updateActivitySelectionForAllParticipantsMutation,
    updateSelectionForAllActivitiesMutation,
    updateEventActivityMutation,
    updateEventActivityOrderMutation,
    copyEventActivitySelectionsMutation,
    exportEventActivityOverridesMutation,
    createEventActivitiesMutation,
    deleteEventActivityMutation,
    importEventFromPreviousEventMutation,
    resetEventSelectionsMutation,
    bulkUpdateActiveMutation,
  } = useTeamEventMutations({
    teamId: team.id,
    eventId: numericEventId,
    applyEventUpdate,
    optimisticEventUpdate,
    onError: (error) => {
      displayErrorToast(error as Error, t)
    },
  })
  const handleChannelEventUpdated = useCallback(
    (nextEvent: wowaudit.Event) => {
      optimisticEventUpdate.applyServerUpdate(nextEvent)
    },
    [optimisticEventUpdate],
  )

  const updateContextValue = {
    isSaving:
      updateEventMutation.isPending ||
      updateStatusMutation.isPending ||
      updateSelectionMutation.isPending ||
      removeGuestMutation.isPending ||
      updateActivitySelectionForAllParticipantsMutation.isPending ||
      updateSelectionForAllActivitiesMutation.isPending ||
      updateEventActivityMutation.isPending ||
      updateEventActivityOrderMutation.isPending ||
      copyEventActivitySelectionsMutation.isPending ||
      exportEventActivityOverridesMutation.isPending ||
      createEventActivitiesMutation.isPending ||
      deleteEventActivityMutation.isPending ||
      importEventFromPreviousEventMutation.isPending ||
      resetEventSelectionsMutation.isPending ||
      bulkUpdateActiveMutation.isPending,
    editEvent: async (values: AddEventFormValues) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateEvent: async (values: Partial<wowaudit.Event>) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateStatus: (
      eventParticipantId: number,
      status: wowaudit.EventParticipantReference['status'],
    ) => {
      updateStatusMutation.mutate({
        eventParticipantId,
        status,
      })
    },
    updateSelection: (
      eventParticipantId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
      activityId?: number,
    ) => {
      updateSelectionMutation.mutate({
        eventParticipantId,
        activityId,
        ...updates,
      })
    },
    updateActivitySelectionForAllParticipants: (
      activityId: number,
      selected: boolean,
    ) => {
      updateActivitySelectionForAllParticipantsMutation.mutate({
        activityId,
        selected,
      })
    },
    updateSelectionForAllActivities: (
      activityIds: number[],
      selected: boolean,
    ) => {
      updateSelectionForAllActivitiesMutation.mutate({
        activityIds,
        selected,
      })
    },
    updateEventActivity: (
      activityId: number,
      updates: UpdateEventActivityPayload,
    ) => {
      return updateEventActivityMutation
        .mutateAsync({
          activityId,
          eventActivity: updates,
        })
        .then(() => undefined)
    },
    updateEventActivityOrder: (activityOrder: number[]) => {
      return updateEventActivityOrderMutation
        .mutateAsync({ activityOrder })
        .then(() => undefined)
    },
    copySelectionsFromActivity: (
      activityId: number,
      sourceActivityId: number,
      sourceEventId?: number,
    ) => {
      return copyEventActivitySelectionsMutation
        .mutateAsync({ activityId, sourceActivityId, sourceEventId })
        .then(() => undefined)
    },
    exportEventActivityOverrides: (activityId: number) => {
      return exportEventActivityOverridesMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    addEventActivities: (eventActivity: CreateEventActivitiesPayload) => {
      return createEventActivitiesMutation
        .mutateAsync({ eventActivity })
        .then(() => undefined)
    },
    removeEventActivity: (activityId: number) => {
      return deleteEventActivityMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    importEventFromPreviousEvent: (sourceEventId: number) => {
      return importEventFromPreviousEventMutation
        .mutateAsync({ sourceEventId })
        .then(() => undefined)
    },
    resetAllSelections: () => {
      return resetEventSelectionsMutation.mutateAsync().then(() => undefined)
    },
    setAllActivitiesActive: (active: boolean) => {
      return bulkUpdateActiveMutation.mutateAsync(active).then(() => undefined)
    },
    removeGuest: (eventParticipantId: number) => {
      removeGuestMutation.mutate(eventParticipantId)
    },
  }
  const sortedActivities = useMemo(() => {
    if (!event?.activities?.length) return []

    return event.activities
      .slice()
      .sort((left, right) => left.order - right.order)
  }, [event?.activities])
  const { importCandidates } = useEventImportCandidates(
    team.id,
    numericEventId ?? 0,
    canAdminEdit && !!event && !event.touched && event.activityType === 'raids',
  )

  const initialEventReference = getReference<wowaudit.EventReference>(
    eventId ? `${team.path}/events/${eventId}` : undefined,
  )

  if (!numericEventId) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  if (!isLoadingEvent && (hasEventError || !event)) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  if (isLoadingEvent) {
    return (
      <Section title={`Event | ${team.name} | ${guild.name}`}>
        <TeamEventSkeleton eventReference={initialEventReference} />
      </Section>
    )
  }

  return (
    <Section title={`Event | ${team.name} | ${guild.name}`}>
      <TeamEventUpdateProvider value={updateContextValue}>
        <TeamEventPlanningInfoProvider
          value={{ showPlanningInfo, setShowPlanningInfo }}
        >
          <EventHeader
            eventId={numericEventId}
            initialEventReference={initialEventReference}
            onAddParticipants={() => setAddParticipantsOpen(true)}
            onAddActivities={() => setAddActivitiesOpen(true)}
            onEventUpdated={handleChannelEventUpdated}
          />

          {event ? (
            <TeamEventParticipantsProvider
              event={event}
              activities={sortedActivities}
              planningInfo={eventActivitiesPlanningInfo}
            >
              <TeamEventLinkedPlanDataProvider
                activities={sortedActivities}
                activityType={event.activityType}
              >
                {({ linkedPlanDataByActivityId, isRaidPlansLoading }) => {
                  const scopedActivity =
                    resolveEventFocusActivity(event, sortedActivities) ??
                    sortedActivities[0]
                  const primaryActivity = sortedActivities.find(
                    (activity) => activity.id === event.primaryActivityId,
                  )

                  return (
                    <>
                      <ViewerSignupCard
                        event={event}
                        activities={sortedActivities}
                        linkedPlanDataByActivityId={linkedPlanDataByActivityId}
                      />

                      {canAdminEdit &&
                      !event.touched &&
                      event.activityType === 'raids' ? (
                        <EventGetStartedBanner
                          event={event}
                          importCandidatesCount={importCandidates.length}
                          onImportClick={() => setIsImportDialogOpen(true)}
                        />
                      ) : null}

                      <div className='mt-4 mb-2 flex justify-between items-end flex-wrap gap-2 max-sm:flex-col max-sm:items-stretch'>
                        <div className='flex flex-col gap-1'>
                          <div className='flex items-stretch gap-2 flex-wrap'>
                            {event ? (
                              <EventAttendanceSummary event={event} />
                            ) : null}
                          </div>
                        </div>

                        <div className='flex flex-col items-end gap-1 max-sm:items-stretch'>
                          {event.activityType === 'raids' &&
                          canAdminEdit &&
                          event.touched &&
                          sortedActivities.length > 1 ? (
                            <EventSetupModeControl event={event} />
                          ) : null}
                          <div className='flex items-end gap-2 flex-wrap'>
                            {event.raidsPlanningScope === 'single' &&
                            event.primaryActivityId != null ? null : (
                              <CopyAllNotesButton
                                activities={sortedActivities}
                                linkedPlanDataByActivityId={
                                  linkedPlanDataByActivityId
                                }
                              />
                            )}

                            {event.activityType === 'raids' && canAdminEdit ? (
                              <>
                                {!(
                                  event.raidsPlanningScope === 'single' &&
                                  event.primaryActivityId == null
                                ) ? (
                                  <Card className='flex items-center gap-2 h-9 px-2'>
                                    <Switch
                                      id='show-planning-info'
                                      checked={showPlanningInfo}
                                      onCheckedChange={(checked) =>
                                        setShowPlanningInfo(checked === true)
                                      }
                                    />
                                    <label
                                      htmlFor='show-planning-info'
                                      className='cursor-pointer select-none'
                                    >
                                      Planning info
                                    </label>
                                  </Card>
                                ) : null}
                                <DropdownMenu modal={false}>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant='outline' size='sm'>
                                      <Settings2Icon className='h-4 w-4' />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align='end'>
                                    <EventBulkSelectionMenuItem />
                                    <DropdownMenuItem
                                      onSelect={() => {
                                        setIsImportDialogOpen(true)
                                      }}
                                    >
                                      Import from previous event
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={() => {
                                        setIsResetSelectionsConfirmOpen(true)
                                      }}
                                    >
                                      Reset all selections
                                    </DropdownMenuItem>
                                    {event.raidsPlanningScope !== 'single' && (
                                      <DropdownMenuItem
                                        onSelect={() => {
                                          const anyActive =
                                            sortedActivities.some(
                                              (a) => a.active,
                                            )
                                          void updateContextValue.setAllActivitiesActive(
                                            !anyActive,
                                          )
                                        }}
                                      >
                                        {sortedActivities.some((a) => a.active)
                                          ? 'Set all encounters inactive'
                                          : 'Set all encounters active'}
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {sortedActivities.length === 0 ? (
                        <Card className='flex flex-col items-center gap-3 mt-2'>
                          <div className='text-sm text-muted-foreground'>
                            No activities found
                          </div>
                          {canAdminEdit ? (
                            <Button
                              size='sm'
                              onClick={() => setAddActivitiesOpen(true)}
                            >
                              Add activity
                            </Button>
                          ) : null}
                        </Card>
                      ) : event.activityType !== 'raids' ? (
                        <EventActivityBlock activity={sortedActivities[0]} />
                      ) : event.raidsPlanningScope === 'per_encounter' ? (
                        <EventActivityGrid
                          activities={sortedActivities}
                          linkedPlanDataByActivityId={
                            linkedPlanDataByActivityId
                          }
                          isRaidPlansLoading={isRaidPlansLoading}
                        />
                      ) : primaryActivity ? (
                        <EventActivitySpecificEncounter
                          activity={primaryActivity}
                          activities={sortedActivities}
                          linkedPlanData={linkedPlanDataByActivityId.get(
                            primaryActivity.id,
                          )}
                          isLinkedPlanDataLoading={isRaidPlansLoading}
                        />
                      ) : (
                        <EventActivitySpecificEncounter
                          activity={scopedActivity}
                          activities={sortedActivities}
                          isLinkedPlanDataLoading={isRaidPlansLoading}
                          allEncounters
                        />
                      )}
                    </>
                  )
                }}
              </TeamEventLinkedPlanDataProvider>

              <AddParticipantModal
                open={addParticipantsOpen}
                setOpen={setAddParticipantsOpen}
                event={event}
              />
              <AddEventActivitiesDialog
                open={addActivitiesOpen}
                setOpen={setAddActivitiesOpen}
                event={event}
                isPending={createEventActivitiesMutation.isPending}
                onSubmit={async (payload) => {
                  await updateContextValue.addEventActivities(payload)
                }}
              />
              {canAdminEdit ? (
                <>
                  <ImportFromPreviousEventDialog
                    event={event}
                    open={isImportDialogOpen}
                    onOpenChange={setIsImportDialogOpen}
                    onImport={async (sourceEventId) => {
                      await updateContextValue.importEventFromPreviousEvent(
                        sourceEventId,
                      )
                    }}
                  />
                  <ConfirmDialog
                    open={isResetSelectionsConfirmOpen}
                    title='Reset all selections?'
                    description='This will set all encounter selections back to the default value for all participants in this event.'
                    variant='destructive'
                    onCancel={() => setIsResetSelectionsConfirmOpen(false)}
                    onContinue={async () => {
                      await updateContextValue.resetAllSelections()
                      setIsResetSelectionsConfirmOpen(false)
                    }}
                  />
                </>
              ) : null}
            </TeamEventParticipantsProvider>
          ) : null}
        </TeamEventPlanningInfoProvider>
      </TeamEventUpdateProvider>
    </Section>
  )
}

export default TeamEvent
